import cx from 'classnames';
import { useAnalytics } from 'libs/utils/site/analytics';
import { ExtremeButtons } from 'pages/custom/Extreme/ExtremeButtons';
import { CustomMapPage } from 'pages/custom/shared/CustomMapPage';
import { OverflowFilters } from 'pages/custom/shared/overflow/OverflowFilters';
import { S_PageStyle } from 'pages/page/PageStyle';
import { useContext } from 'react';
import { Layer, Source } from 'react-map-gl';
import { CtxView } from 'session/ctxView';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const STARTUP_EXTREME_BUILDING = {
    name: 'SX',
    floors: [
        { name: 'Ground floor', setId: 'b5a778b2-76f3-4b90-8ca0-78f71c32c538' },
        { name: 'Minus 1', setId: 'e766f852-7649-454e-9181-54d1daea8b6b' },
        { name: 'Minus 2', setId: '0cd17c62-bc2f-45a0-859d-0e9bb6190893' }
    ]
};

export const ExtremePage = (props: Props): JSX.Element => {
    const ctxView = useContext(CtxView);
    const analytics = useAnalytics();

    const [floor1, floor2, floor3] = STARTUP_EXTREME_BUILDING.floors;

    function isSetHighlighted(mapSetId: string) {
        return ctxView.visibleMapSetIds.includes(mapSetId);
    }

    function setVisibleMapSetAndHideOthers(mapSetId: string) {
        const visible = ctxView.visibleMapSetIds;
        const newVisible = visible.filter(
            (id) => id !== floor1.setId && id !== floor2.setId && id !== floor3.setId
        );
        newVisible.push(mapSetId);
        ctxView.setVisibleMapSetIds(newVisible);

        analytics.track('floor_button.clicked', {
            building: 'SX',
            source: 'custom_buttons',
            floorId: mapSetId
        });
    }

    return (
        <S_ExtremePage className={cx(props.className, 'cleanmap')}>
            <CustomMapPage
                mapId={'2874985c-30e9-41d6-a999-70f4251f750b'}
                controls={<ExtremeButtons />}
                onMarkerSelected={(markerId: string) => {
                    if (markerId === '0.6799752427377139') {
                        setVisibleMapSetAndHideOthers(floor2.setId);
                        return true;
                    } else if (markerId === '0.35242149506731013') {
                        setVisibleMapSetAndHideOthers(floor1.setId);
                        return true;
                    } else if (markerId === '0.20536375027725473') {
                        setVisibleMapSetAndHideOthers(floor2.setId);
                        return true;
                    } else if (markerId === '0.3653803481633937') {
                        setVisibleMapSetAndHideOthers(floor3.setId);
                        return true;
                    }
                }}
                hideFloors
                topOverflow={
                    <OverflowFilters
                        highlightedBackground="linear-gradient(90deg, #515151 0%, #2d2d2d 100%)"
                        items={[
                            {
                                iconName: 'layer-group',
                                text: 'First floor',
                                onClick: () => {
                                    setVisibleMapSetAndHideOthers(floor1.setId);
                                },
                                highlighted: isSetHighlighted(floor1.setId)
                            },
                            {
                                iconName: 'layer-group',
                                text: 'Ground floor',
                                onClick: () => {
                                    setVisibleMapSetAndHideOthers(floor2.setId);
                                },
                                highlighted: isSetHighlighted(floor2.setId)
                            },
                            {
                                iconName: 'layer-group',
                                text: 'Basement',
                                onClick: () => {
                                    setVisibleMapSetAndHideOthers(floor3.setId);
                                },
                                highlighted: isSetHighlighted(floor3.setId)
                            }
                        ]}
                    />
                }
                mapChildren={
                    <>
                        <Source
                            id="polylineLayer"
                            type="geojson"
                            data={{
                                type: 'Feature',
                                properties: {},
                                geometry: {
                                    type: 'LineString',
                                    coordinates: [
                                        [8.522192, 60.861676],
                                        [8.521383, 60.861763],
                                        [8.520535, 60.861906],
                                        [8.520024, 60.861948],
                                        [8.519163, 60.862419],
                                        [8.517926, 60.862378],
                                        [8.517392, 60.862315],
                                        [8.516891, 60.862209],
                                        [8.516401, 60.862191]
                                    ]
                                }
                            }}
                        >
                            <Layer
                                id="lineLayer"
                                type="line"
                                source="polylineLayer"
                                paint={{
                                    'line-color': '#CDCDCD',
                                    'line-width': 2.5,
                                    'line-dasharray': [2, 1.5]
                                }}
                            />
                        </Source>
                        <Source
                            id="polylineLayer2"
                            type="geojson"
                            data={{
                                type: 'Feature',
                                properties: {},
                                geometry: {
                                    type: 'LineString',
                                    coordinates: [
                                        [8.523068, 60.861822],
                                        [8.523602019718027, 60.861505558177555],
                                        [8.524012774344897, 60.86118795310304],
                                        [8.524041954612358, 60.86083624875471],
                                        [8.523577114494003, 60.86047814586365],
                                        [8.522621169428731, 60.86024304978079],
                                        [8.521149025094644, 60.86033469078728],
                                        [8.519803580052638, 60.86021645131285],
                                        [8.519128955231196, 60.86004689115944],
                                        [8.518527481880739, 60.85989976177564],
                                        [8.517854849111927, 60.85999198205411],
                                        [8.517324252990619, 60.85997437148251]
                                    ]
                                }
                            }}
                        >
                            <Layer
                                id="lineLayer2"
                                type="line"
                                source="polylineLayer2"
                                paint={{
                                    'line-color': '#CDCDCD',
                                    'line-width': 2.5,
                                    'line-dasharray': [2, 1.5]
                                }}
                            />
                        </Source>
                        <Source
                            id="polylineLayer3"
                            type="geojson"
                            data={{
                                type: 'Feature',
                                properties: {},
                                geometry: {
                                    type: 'LineString',
                                    coordinates: [
                                        [8.524002617960946, 60.86118282756263],
                                        [8.52412085959179, 60.861172410900195],
                                        [8.524157007282582, 60.861006568968406],
                                        [8.525718146377613, 60.860885354342116],
                                        [8.526895537397309, 60.86053293141953],
                                        [8.528528129996175, 60.8604976219809],
                                        [8.530785273533212, 60.86028615231865],
                                        [8.532932053219838, 60.860427209547595],
                                        [8.534516401634733, 60.86041110767076]
                                    ]
                                }
                            }}
                        >
                            <Layer
                                id="lineLayer3"
                                type="line"
                                source="polylineLayer3"
                                paint={{
                                    'line-color': '#CDCDCD',
                                    'line-width': 2.5,
                                    'line-dasharray': [2, 1.5]
                                }}
                            />
                        </Source>
                    </>
                }
            />
        </S_ExtremePage>
    );
};

const S_ExtremePage = styled(S_PageStyle)`
    display: flex;
    flex-direction: column;
`;
